import { catchError, debounceTime, flatMap, switchMap } from "rxjs/operators"
import { combineEpics, ofType, StateObservable } from "redux-observable"
import { concat, EMPTY, from, of } from "rxjs"
import { Epic } from "../../epics"

import {
    verifyIfBookmarkGridTile,
    verifyIfWorkspaceContentGridTile,
    verifyIfShortcutGridTile,
    verifyIfCustomContentWidgetGridTile,
} from "../../apis/grid"

import * as actions from "./actions"
import { addErrorNotification } from "../notifications/actions"
import { GlobalState } from "../../store"
import { initializeBookmarks } from "../bookmarks/actions"
import { initializeShortcuts } from "../shortcuts/actions"
import { getTileIds } from "../../util/Grid"
import { RequestCancelledError } from "../../errors"
import { IGrid, NavigationWorkspaceChild } from "../../models"
import { GridTileDTO } from "../../apis/models/dto/grid"

export const defaultRequestingWorkspaceContentErrorMessage =
    "Something went wrong when fetching the workspace content."
export const defaultRequestingWorkspaceGridErrorMessage =
    "Something went wrong when fetching the workspace grid."
export const defaultRequestingWorkspaceGridReorderErrorMessage =
    "Something went wrong when reordering the workspace grid."

const filterGridTiles = (tile: GridTileDTO): boolean =>
    verifyIfBookmarkGridTile(tile) ||
    verifyIfWorkspaceContentGridTile(tile) ||
    verifyIfShortcutGridTile(tile) ||
    verifyIfCustomContentWidgetGridTile(tile)

export const initializeEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    _dependencies$,
) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        switchMap(() => {
            const { filter, offset, limit } =
                state$.value.workspaceCoverPage.content
            return concat(
                of(initializeBookmarks()),
                of(initializeShortcuts()),
                of(actions.requestWorkspaceContent(filter, offset, limit)),
                of(actions.requestCoverPageGrid()),
            )
        }),
    )

export const requestWorkspaceContentEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { workspaceCoverPage: { navigationAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_WORKSPACE_CONTENT),
        debounceTime(500),
        switchMap(
            ({ filter, offset, limit }: actions.RequestWorkspaceContent) => {
                const { workspaceId } = state$.value.workspaceCoverPage

                return from(
                    navigationAPI.getWorkspaceContent(
                        workspaceId,
                        filter,
                        offset,
                        limit + 1,
                    ),
                ).pipe(
                    flatMap((children: NavigationWorkspaceChild[]) => {
                        const hasMore = children.length > limit
                        children = children.slice(0, limit)
                        return of(
                            actions.receiveWorkspaceContent(children, hasMore),
                        )
                    }),
                    catchError((error) => {
                        if (error instanceof RequestCancelledError) {
                            return EMPTY
                        }
                        console.error(error)
                        return concat(
                            of(
                                actions.receiveWorkspaceContent(
                                    state$.value.workspaceCoverPage.content
                                        .children || [],
                                    false,
                                ),
                            ),
                            of(
                                addErrorNotification(
                                    defaultRequestingWorkspaceContentErrorMessage,
                                ),
                            ),
                        )
                    }),
                )
            },
        ),
    )

export const requestCoverPageGridEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { workspaceCoverPage: { gridAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_COVERPAGE_GRID),
        switchMap(() => {
            const { workspaceId } = state$.value.workspaceCoverPage

            return from(
                workspaceId === 0
                    ? gridAPI.getDashboardCoverPageGrid(filterGridTiles)
                    : gridAPI.getWorkspaceCoverPageGrid(
                          workspaceId,
                          filterGridTiles,
                      ),
            ).pipe(
                flatMap((grid: IGrid) =>
                    of(actions.receiveCoverPageGrid(grid)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveCoverPageGrid({
                                id: -1,
                                tiles: [],
                            }),
                        ),
                        of(
                            addErrorNotification(
                                defaultRequestingWorkspaceGridErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )
export const gridUpdateTilesOrderEpic: Epic = (
    action$,
    state$: StateObservable<GlobalState>,
    { workspaceCoverPage: { gridAPI } },
) =>
    action$.pipe(
        ofType(actions.UPDATE_TILES_ORDER),
        switchMap(() => {
            const grid = state$.value.workspaceCoverPage.grid
            const tileIds = getTileIds(grid.tiles)

            return from(
                gridAPI.updateTilesOrder(grid.id, tileIds, filterGridTiles),
            ).pipe(
                flatMap((grid: IGrid) =>
                    of(actions.receiveCoverPageGrid(grid)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return concat(
                        of(
                            actions.receiveCoverPageGrid(
                                state$.value.workspaceCoverPage.grid,
                            ),
                        ),
                        of(
                            addErrorNotification(
                                defaultRequestingWorkspaceGridReorderErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export default combineEpics(
    initializeEpic,
    requestWorkspaceContentEpic,
    requestCoverPageGridEpic,
    gridUpdateTilesOrderEpic,
)
